import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "./pd-rating-stars.js";
import { LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import "@progressive-development/pd-icon/pd-icon";
import { pdIcons } from "@progressive-development/pd-icon";
import { getCurrentFormatLocale } from "@progressive-development/pd-utils";
//#region src/pd-rating/PdRatingOverview.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
var PdRatingOverview = class PdRatingOverview extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.loading = false;
		this.interactive = true;
		this.variant = "default";
	}
	static {
		this.styles = css`
    :host {
      display: inline-block;
    }

    .overview {
      display: inline-flex;
      align-items: center;
      gap: var(--pd-rating-overview-gap, 0.5rem);
      font: inherit;
      color: inherit;
      border: none;
      background: none;
      padding: 0;
      margin: 0;
    }

    button.overview {
      cursor: pointer;
      border-radius: var(--pd-radius-sm, 2px);
    }

    button.overview:hover .count {
      text-decoration: underline;
    }

    button.overview:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col, currentColor);
      outline-offset: 2px;
    }

    .value {
      font-weight: 600;
    }

    .count {
      color: var(--pd-default-font-muted-col, #757575);
    }

    .empty {
      color: var(--pd-default-font-muted-col, #757575);
      font-style: italic;
    }

    .skeleton {
      width: 12rem;
      height: 1.4rem;
      border-radius: 4px;
      background: var(--pd-default-bg-light-col, #eee);
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      50% {
        opacity: 0.5;
      }
    }

    /* Compact: ein Stern + "4,3 (12)" — erbt Schriftgröße/-farbe vom
     * Kontext (Karten-Zeilen), Stern skaliert mit der Schrift. */
    :host([variant="compact"]) .overview {
      gap: var(--pd-rating-overview-gap, 0.2rem);
    }

    :host([variant="compact"]) .skeleton {
      width: 3.5rem;
      height: 1em;
    }

    :host([variant="compact"]) .value,
    :host([variant="compact"]) .count {
      font-weight: inherit;
      color: inherit;
    }

    .compact-star {
      --pd-icon-size: var(--pd-rating-star-size, 1em);
      --pd-icon-col: var(
        --pd-rating-star-col,
        var(--pd-default-warning-col, #f0a417)
      );
      --pd-icon-col-hover: var(
        --pd-rating-star-col,
        var(--pd-default-warning-col, #f0a417)
      );
      padding: 0;
    }
  `;
	}
	render() {
		if (this.loading) return html`
        <div
          class="skeleton"
          role="status"
          aria-label="${msg("Bewertungen werden geladen", { id: "pd.rating.overview.loading" })}"
        ></div>
      `;
		const summary = this.summary;
		if (!summary) return nothing;
		if (summary.count === 0) {
			if (this.variant === "compact") return nothing;
			return html`
        <span class="empty">
          ${msg("Noch keine Bewertungen", { id: "pd.rating.overview.empty" })}
        </span>
      `;
		}
		const content = this.variant === "compact" ? this._renderCompactContent(summary) : this._renderContent(summary);
		if (!this.interactive) return html`
        <span
          class="overview"
          role="img"
          aria-label="${this._ariaText(summary)}"
        >
          ${content}
        </span>
      `;
		return html`
      <button
        class="overview"
        aria-label="${this._ariaLabel(summary)}"
        @click="${this._emitShowList}"
      >
        ${content}
      </button>
    `;
	}
	_renderContent(summary) {
		return html`
      ${summary.average !== null ? html`
              <pd-rating-stars
                .value="${summary.average}"
                aria-hidden="true"
              ></pd-rating-stars>
              <span class="value">${this._formatAverage(summary.average)}</span>
            ` : nothing}
      <span class="count">${this._countLabel(summary)}</span>
    `;
	}
	/** Compact anatomy: one filled star + "4,3 (12)"; text-only "– (N)". */
	_renderCompactContent(summary) {
		return html`
      <pd-icon
        class="compact-star"
        icon="${pdIcons.ICON_STAR}"
        aria-hidden="true"
      ></pd-icon>
      <span class="value" aria-hidden="true">
        ${summary.average !== null ? this._formatAverage(summary.average) : "–"}
      </span>
      <span class="count" aria-hidden="true">(${summary.count})</span>
    `;
	}
	_countLabel(summary) {
		const ratedCount = summary.ratedCount ?? summary.count;
		if (ratedCount < summary.count) return msg(str`${summary.count} Berichte · ${ratedCount} mit Bewertung`, { id: "pd.rating.overview.countMixed" });
		return summary.count === 1 ? msg("1 Bewertung", { id: "pd.rating.overview.countOne" }) : msg(str`${summary.count} Bewertungen`, { id: "pd.rating.overview.count" });
	}
	/** Accessible sentence without the action suffix (pure display). */
	_ariaText(summary) {
		const count = this._countLabel(summary);
		return summary.average !== null ? msg(str`Bewertung: ${this._formatAverage(summary.average)} von 5 Sternen, ${count}`, { id: "pd.rating.overview.ariaRated" }) : count;
	}
	_ariaLabel(summary) {
		return msg(str`${this._ariaText(summary)} — Liste anzeigen`, { id: "pd.rating.overview.ariaAction" });
	}
	_formatAverage(average) {
		return average.toLocaleString(getCurrentFormatLocale(), {
			minimumFractionDigits: 1,
			maximumFractionDigits: 1
		});
	}
	_emitShowList() {
		this.dispatchEvent(new CustomEvent("pd-rating-show-list", {
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ attribute: false })], PdRatingOverview.prototype, "summary", void 0);
__decorate([property({ type: Boolean })], PdRatingOverview.prototype, "loading", void 0);
__decorate([property({ type: Boolean })], PdRatingOverview.prototype, "interactive", void 0);
__decorate([property({ reflect: true })], PdRatingOverview.prototype, "variant", void 0);
PdRatingOverview = __decorate([localized()], PdRatingOverview);
//#endregion
export { PdRatingOverview };
